// frontend/src/components/Header.jsx
// UPDATED: Added ChatButton integration

import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import ChatButton from "./chat/ChatButton";
import "../assets/css/header.css";

const Header = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const { user, logout, loadingUser } = useAuth();

  // Get tenant name with multiple fallbacks
  const tenantName =
    user?.tenant_name ||
    user?.tenantName ||
    user?.tenant?.tenant_name ||
    user?.tenant?.name ||
    "My Organization";

  // Get user display name with smart fallbacks
  const getUserDisplayName = () => {
    if (!user) return "User";

    if (
      user.name &&
      typeof user.name === "string" &&
      user.name.trim().length > 0
    ) {
      return user.name.replace(/\s*-\s*Admin\s*$/i, "").trim();
    }

    if (user.email) {
      const emailName = user.email.split("@")[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }

    return "User";
  };

  const userName = getUserDisplayName();

  // Format role display
  const getRoleDisplay = () => {
    if (!user?.role) return null;

    const formattedRole = user.role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    return formattedRole;
  };

  const roleDisplay = getRoleDisplay();

  // Get role badge color based on role type
  const getRoleBadgeColor = () => {
    const role = user?.role?.toLowerCase();

    if (role === "admin") return "danger";
    if (role === "supervisor" || role === "manager") return "warning";
    if (role === "employee" || role === "staff") return "primary";
    if (role === "job_coach") return "info";
    if (role === "hr") return "success";

    return "secondary";
  };

  const roleBadgeColor = getRoleBadgeColor();

  // Determine environment based on URL
  const getEnvironment = () => {
    const hostname = window.location.hostname;

    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return { name: "LOCAL", color: "success" };
    } else if (hostname.includes("trn.") || hostname.includes("staging")) {
      return { name: "TRN", color: "warning" };
    } else if (hostname.includes("prd.") || hostname === "webvenly.com") {
      return { name: "PRD", color: "danger" };
    } else {
      return { name: "DEV", color: "info" };
    }
  };

  const environment = getEnvironment();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error("❌ Logout error in Header:", err.message);
    }
  };

  // Display loading state while user is being fetched
  if (loadingUser) {
    return (
      <header className="header-container px-3">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center">
            <button
              className="btn toggle-sidebar-btn me-3"
              onClick={toggleSidebar}
              aria-label="Toggle Sidebar"
              disabled
            >
              <i className="bi bi-list"></i>
            </button>
            <div
              className="spinner-border spinner-border-sm text-muted me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="text-muted">Loading...</span>
          </div>
        </div>
      </header>
    );
  }

  // If not logged in, display minimal header
  if (!user) {
    return (
      <header className="header-container px-3">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center">
            <button
              className="btn toggle-sidebar-btn me-3"
              onClick={toggleSidebar}
              aria-label="Toggle Sidebar"
              disabled
            >
              <i className="bi bi-list"></i>
            </button>
            <span className="text-muted">Not signed in</span>
          </div>
          <Link to="/login" className="btn btn-outline-primary btn-sm">
            Sign In
          </Link>
        </div>
      </header>
    );
  }

  // Main logged-in header
  return (
    <header className="header-container px-3 px-md-4 px-lg-5">
      {/* Row 1: Toggle + Tenant Name / Environment Badge / Chat / Profile / Logout */}
      <div className="d-flex justify-content-between align-items-center w-100 flex-wrap">
        {/* Left: Toggle + Tenant Name + Environment */}
        <div className="d-flex align-items-center">
          <button
            className="btn toggle-sidebar-btn me-3"
            onClick={toggleSidebar}
            aria-label="Toggle Sidebar"
          >
            <i className="bi bi-list"></i>
          </button>
          <h5 className="tenant-name mb-0">{tenantName}</h5>
          {/* Environment Badge */}
          <span className={`badge bg-${environment.color} ms-2`}>
            {environment.name}
          </span>
        </div>

        {/* Right (Desktop): Chat + Profile + Logout */}
        <div className="user-section d-none d-md-flex align-items-center">
          {/* Chat Button */}
          <ChatButton />

          <Link
            to="/crm/profile"
            className="me-3 text-decoration-none text-dark d-flex align-items-center"
            title={`Profile: ${user.email}`}
          >
            <i className="bi bi-person-circle me-1"></i>
            <span className="user-name-display">{userName}</span>
            {roleDisplay && (
              <span className={`badge bg-${roleBadgeColor} ms-2 small`}>
                {roleDisplay}
              </span>
            )}
          </Link>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={handleLogout}
          >
            <i className="bi bi-box-arrow-right me-1"></i>
            Logout
          </button>
        </div>
      </div>

      {/* Row 2 (Mobile): Chat + Profile + Logout */}
      <div className="user-section d-flex d-md-none align-items-center justify-content-center w-100 mt-2 flex-wrap">
        {/* Chat Button */}
        <ChatButton />

        <Link
          to="/crm/profile"
          className="me-3 text-decoration-none text-dark d-flex align-items-center"
          title={`Profile: ${user.email}`}
        >
          <i className="bi bi-person-circle me-1"></i>
          <span className="user-name-display">{userName}</span>
          {roleDisplay && (
            <span className={`badge bg-${roleBadgeColor} ms-2 small`}>
              {roleDisplay}
            </span>
          )}
        </Link>
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={handleLogout}
        >
          <i className="bi bi-box-arrow-right me-1"></i>
          Logout
        </button>
      </div>
    </header>
  );
};

export default Header;
